import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Keyboard, KeyboardAvoidingView, Platform, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Loading from './Loading';
import useCustomTheme from '../../hooks/theme-hook';
import { ScrollView } from 'react-native-gesture-handler';
import { useAppDispatch } from '../../hooks/store-hook';
import { drawerActions } from '../../store/drawerSlice';

export interface PopupProps {
  loading?: boolean;
  children: ReactElement;
  suspendDrawer?: boolean;
  scrollable?: boolean;
  backgroundColor?: string;
  borderRadius?: number;
}

const Popup = (props: PopupProps) => {
  const theme = useCustomTheme();
  const layout = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const dispatch = useAppDispatch();
  const isDarkMode = !!theme.isDark;
  const isAndroid = Platform.OS === 'android';
  const enableKeyboardAvoidance = Platform.OS === 'ios';
  const keyboardAvoidingBehavior: 'height' | 'position' | 'padding' | undefined = Platform.OS === 'ios' ? 'padding' : undefined;
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const baseWindowHeightRef = useRef(layout.height);

  useEffect(() => {
    if (!isAndroid || keyboardHeight !== 0) {
      return;
    }

    baseWindowHeightRef.current = Math.max(baseWindowHeightRef.current, layout.height);
  }, [isAndroid, keyboardHeight, layout.height]);

  useEffect(() => {
    if (props.suspendDrawer === false) {
      return;
    }

    dispatch(drawerActions.incrementSuspendCount());

    return () => {
      dispatch(drawerActions.decrementSuspendCount());
    };
  }, [dispatch, props.suspendDrawer]);

  useEffect(() => {
    if (!isAndroid) {
      return;
    }

    const showSubscription = Keyboard.addListener('keyboardDidShow', (event) => {
      setKeyboardHeight(Math.max(event.endCoordinates?.height ?? 0, 0));
    });
    const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, [isAndroid]);

  const adjustedKeyboardHeight = isAndroid ? Math.max(keyboardHeight - insets.bottom, 0) : 0;
  const availableHeight = isAndroid
    ? Math.max(Math.min(layout.height, baseWindowHeightRef.current - adjustedKeyboardHeight), 0)
    : layout.height;
  const popupTopMargin = 20 + Math.max(insets.top, 0);
  const popupBottomMargin = 20 + Math.max(insets.bottom, 0);
  const popupMaxHeight = Math.max(availableHeight - popupTopMargin - popupBottomMargin, 180);
  const keyboardTranslateY = isAndroid && adjustedKeyboardHeight > 0 ? -adjustedKeyboardHeight / 2 : 0;

  const content = (
    <>
      {props.loading && (
        <Loading
          style={{
            width: '100%',
            height: '100%',
            // Match dark mode surfaces (avoid the light teal overlay).
            backgroundColor: isDarkMode ? 'rgba(24, 25, 26, 0.85)' : 'rgba(233, 245, 243, 0.8)',
            zIndex: 4000,
          }}
        />
      )}
      {props.children}
    </>
  );

  return (
    <KeyboardAvoidingView
      enabled={enableKeyboardAvoidance}
      style={{
        borderRadius: props.borderRadius ?? 8,
        backgroundColor: props.backgroundColor ?? theme.background.modal,
        overflow: 'hidden',
        zIndex: 2000,
        maxHeight: popupMaxHeight,
        marginTop: popupTopMargin,
        marginBottom: popupBottomMargin,
        transform: [{ translateY: keyboardTranslateY }],
      }}
      behavior={enableKeyboardAvoidance ? keyboardAvoidingBehavior : undefined}
    >
      {props.scrollable === false ? (
        <View>{content}</View>
      ) : (
        <ScrollView
          style={{ flexGrow: 0, maxHeight: '100%' }}
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled
          showsVerticalScrollIndicator={true}
          bounces={false}
          alwaysBounceVertical={false}
          alwaysBounceHorizontal={false}
          overScrollMode="never"
        >
          {content}
        </ScrollView>
      )}
    </KeyboardAvoidingView>
  );
};

export default Popup;
